import { useState } from "react"
import {
    Menu,
    Typography,
    Button,
    Space,
    Card,
    Switch,
    Checkbox,
    Table,
    Dropdown,
    Upload,
    message,
    Modal,
} from "antd"
import {
    UploadOutlined,
    DownloadOutlined,
} from "@ant-design/icons"
import moment from "moment"

const { Title, Text } = Typography
const { Dragger } = Upload

// Helper for Ant Design message
const showMessage = (type, content) => {
    message[type](content)
}

const BackupExport = () => {
    const [cloudBackupEnabled, setCloudBackupEnabled] = useState(false)
    const [selectedCloudServices, setSelectedCloudServices] = useState([])

    const exportMenu = (
        <Menu onClick={({ key }) => showMessage("success", `Exporting settings as ${key}`)}>
            <Menu.Item key=".json">.json</Menu.Item>
            <Menu.Item key=".yaml">.yaml</Menu.Item>
            <Menu.Item key="include_logs">Include system logs</Menu.Item>
        </Menu>
    )

    const backupHistoryData = [
        { key: "1", id: "#2193", date: "June 12", source: "Manual", action: "Download" },
        { key: "2", id: "#2192", date: "June 7", source: "Auto (GDrive)", action: "Restore" },
        { key: "3", id: "#2191", date: "May 30", source: "Auto (AWS S3)", action: "Download" },
    ]

    const backupHistoryColumns = [
        { title: "Backup ID", dataIndex: "id", key: "id" },
        { title: "Date", dataIndex: "date", key: "date" },
        { title: "Source", dataIndex: "source", key: "source" },
        {
            title: "Action",
            dataIndex: "action",
            key: "action",
            render: (text) => (
                <Button type="link" size="small">
                    {text}
                </Button>
            ),
        },
    ]

    const draggerProps = {
        name: "file",
        multiple: false,
        action: "https://www.mocky.io/v2/5cc8019d300000980a055e76", // Mock upload URL
        onChange(info) {
            const { status } = info.file
            if (status === "done") {
                showMessage("success", `${info.file.name} file uploaded successfully.`)
                Modal.confirm({
                    title: "Import Settings",
                    content: "File uploaded. Do you want to import and apply these settings?",
                    onOk: () => showMessage("success", "Settings imported and applied."),
                })
            } else if (status === "error") {
                showMessage("error", `${info.file.name} file upload failed.`)
            }
        },
        beforeUpload(file) {
            const isJsonOrYaml = file.type === "application/json" || file.name.endsWith(".yaml") || file.name.endsWith(".yml")
            if (!isJsonOrYaml) {
                message.error("You can only upload JSON or YAML files!")
            }
            return isJsonOrYaml || Upload.LIST_IGNORE
        },
    }

    return (
        <Card className="mb-6">
            <div className="mb-4">
                <Title level={4}>Backup & Export</Title>
                <Text type="secondary">Secure your system configuration with backups and restore options.</Text>
            </div>

            <Title level={5} className="mb-4">
                Export Settings
            </Title>
            <Dropdown overlay={exportMenu} placement="bottomLeft">
                <Button icon={<DownloadOutlined />}>Export Settings</Button>
            </Dropdown>
            <Text type="secondary" className="block mt-2 text-sm">
                Last export: {moment().format("MMMM D, YYYY HH:mm A")}
            </Text>

            <Title level={5} className="mt-6 mb-4">
                Import Settings
            </Title>
            <Dragger {...draggerProps}>
                <p className="ant-upload-drag-icon">
                    <UploadOutlined />
                </p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">Allowed formats: .json, .yaml</p>
            </Dragger>

            <Title level={5} className="mt-6 mb-4">
                Cloud Backup
            </Title>
            <div className="flex items-center justify-between mb-4">
                <Text strong>Enable Automatic Cloud Backup</Text>
                <Switch checked={cloudBackupEnabled} onChange={setCloudBackupEnabled} />
            </div>
            {cloudBackupEnabled && (
                <Checkbox.Group onChange={setSelectedCloudServices} value={selectedCloudServices}>
                    <Space direction="vertical">
                        <Checkbox value="google_drive">Google Drive</Checkbox>
                        <Checkbox value="aws_s3">AWS S3</Checkbox>
                        <Checkbox value="dropbox">Dropbox</Checkbox>
                    </Space>
                </Checkbox.Group>
            )}

            <Title level={5} className="mt-6 mb-4">
                Backup History
            </Title>
            <Table dataSource={backupHistoryData} columns={backupHistoryColumns} pagination={false} />
        </Card>
    )
}

export default BackupExport;